import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Baby, Calendar, Clock, Gift, Heart } from 'lucide-react';
import { Button } from '../ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';

interface AgeDetails {
  years: number;
  months: number;
  days: number;
  totalDays: number;
  totalWeeks: number;
  totalHours: number;
  totalMinutes: number;
  totalSeconds: number;
  nextBirthday: Date;
  daysUntilBirthday: number;
  zodiacSign: string;
  chineseZodiac: string;
  lifeStage: string;
}

export const AgeCalculatorTool: React.FC = () => {
  const { t } = useTranslation();
  const [birthDate, setBirthDate] = useState('');
  const [targetDate, setTargetDate] = useState('');
  const [ageDetails, setAgeDetails] = useState<AgeDetails | null>(null);
  const [useCurrentDate, setUseCurrentDate] = useState(true);

  const zodiacSigns = [
    { name: 'Capricorn', start: [12, 22], end: [1, 19] },
    { name: 'Aquarius', start: [1, 20], end: [2, 18] },
    { name: 'Pisces', start: [2, 19], end: [3, 20] },
    { name: 'Aries', start: [3, 21], end: [4, 19] },
    { name: 'Taurus', start: [4, 20], end: [5, 20] },
    { name: 'Gemini', start: [5, 21], end: [6, 20] },
    { name: 'Cancer', start: [6, 21], end: [7, 22] },
    { name: 'Leo', start: [7, 23], end: [8, 22] },
    { name: 'Virgo', start: [8, 23], end: [9, 22] },
    { name: 'Libra', start: [9, 23], end: [10, 22] },
    { name: 'Scorpio', start: [10, 23], end: [11, 21] },
    { name: 'Sagittarius', start: [11, 22], end: [12, 21] }
  ];

  const chineseZodiacAnimals = [
    'Rat', 'Ox', 'Tiger', 'Rabbit', 'Dragon', 'Snake',
    'Horse', 'Goat', 'Monkey', 'Rooster', 'Dog', 'Pig'
  ];

  const getZodiacSign = (month: number, day: number): string => {
    for (const sign of zodiacSigns) {
      const [startMonth, startDay] = sign.start;
      const [endMonth, endDay] = sign.end;
      
      if (startMonth === endMonth) {
        if (month === startMonth && day >= startDay && day <= endDay) {
          return sign.name;
        }
      } else {
        if ((month === startMonth && day >= startDay) || 
            (month === endMonth && day <= endDay)) {
          return sign.name;
        }
      }
    }
    return 'Unknown';
  };

  const getChineseZodiac = (year: number): string => {
    // Chinese zodiac starts from 1900 (Rat year)
    const baseYear = 1900;
    const index = (year - baseYear) % 12;
    return chineseZodiacAnimals[index];
  };

  const getLifeStage = (years: number): string => {
    if (years < 1) return 'Infant';
    if (years < 3) return 'Toddler';
    if (years < 6) return 'Preschooler';
    if (years < 13) return 'Child';
    if (years < 20) return 'Teenager';
    if (years < 30) return 'Young Adult';
    if (years < 50) return 'Adult';
    if (years < 65) return 'Middle-aged';
    return 'Senior';
  };

  const calculateAge = (birthDate: Date, targetDate: Date): AgeDetails => {
    const birthTime = birthDate.getTime();
    const targetTime = targetDate.getTime();
    
    if (targetTime < birthTime) {
      throw new Error('Target date cannot be before birth date');
    }

    // Calculate precise age
    let years = targetDate.getFullYear() - birthDate.getFullYear();
    let months = targetDate.getMonth() - birthDate.getMonth();
    let days = targetDate.getDate() - birthDate.getDate();

    if (days < 0) {
      months--;
      const prevMonth = new Date(targetDate.getFullYear(), targetDate.getMonth(), 0);
      days += prevMonth.getDate();
    }

    if (months < 0) {
      years--;
      months += 12;
    }

    // Calculate totals
    const timeDiff = targetTime - birthTime;
    const totalDays = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
    const totalWeeks = Math.floor(totalDays / 7);
    const totalHours = Math.floor(timeDiff / (1000 * 60 * 60));
    const totalMinutes = Math.floor(timeDiff / (1000 * 60));
    const totalSeconds = Math.floor(timeDiff / 1000);

    // Calculate next birthday
    const nextBirthday = new Date(targetDate.getFullYear(), birthDate.getMonth(), birthDate.getDate());
    if (nextBirthday <= targetDate) {
      nextBirthday.setFullYear(targetDate.getFullYear() + 1);
    }

    const daysUntilBirthday = Math.ceil((nextBirthday.getTime() - targetTime) / (1000 * 60 * 60 * 24));

    // Get zodiac signs
    const zodiacSign = getZodiacSign(birthDate.getMonth() + 1, birthDate.getDate());
    const chineseZodiac = getChineseZodiac(birthDate.getFullYear());
    const lifeStage = getLifeStage(years);

    return {
      years,
      months,
      days,
      totalDays,
      totalWeeks,
      totalHours,
      totalMinutes,
      totalSeconds,
      nextBirthday,
      daysUntilBirthday,
      zodiacSign,
      chineseZodiac,
      lifeStage
    };
  };

  const handleCalculate = () => {
    if (!birthDate) return;

    try {
      const birth = new Date(birthDate);
      const target = useCurrentDate ? new Date() : new Date(targetDate);
      
      if (isNaN(birth.getTime()) || isNaN(target.getTime())) {
        throw new Error('Invalid date');
      }

      const details = calculateAge(birth, target);
      setAgeDetails(details);
    } catch (error) {
      setAgeDetails(null);
    }
  };

  const setToday = () => {
    const today = new Date().toISOString().split('T')[0];
    setTargetDate(today);
  };

  const getAgeEmoji = (years: number): string => {
    if (years < 1) return '👶';
    if (years < 13) return '🧒';
    if (years < 20) return '👦';
    if (years < 30) return '👨';
    if (years < 50) return '👨‍💼';
    if (years < 65) return '👨‍🦳';
    return '👴';
  };

  // Auto-calculate when inputs change
  useEffect(() => {
    handleCalculate();
  }, [birthDate, targetDate, useCurrentDate]);

  // Update current time every second if using current date
  useEffect(() => {
    if (useCurrentDate) {
      const interval = setInterval(() => {
        handleCalculate();
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [useCurrentDate, birthDate]);

  return (
    <div className="space-y-6">
      {/* Input Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Baby className="w-5 h-5" />
            <span>Age Calculator</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Birth Date
              </label>
              <input
                type="date"
                value={birthDate}
                onChange={(e) => setBirthDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Calculate Age As Of
              </label>
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <input
                    type="radio"
                    id="current-date"
                    checked={useCurrentDate}
                    onChange={() => setUseCurrentDate(true)}
                    className="text-primary-600 focus:ring-primary-500"
                  />
                  <label htmlFor="current-date" className="text-sm">Current Date (Live)</label>
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="radio"
                    id="specific-date"
                    checked={!useCurrentDate}
                    onChange={() => setUseCurrentDate(false)}
                    className="text-primary-600 focus:ring-primary-500"
                  />
                  <label htmlFor="specific-date" className="text-sm">Specific Date</label>
                </div>
                {!useCurrentDate && (
                  <div className="flex space-x-2">
                    <input
                      type="date"
                      value={targetDate}
                      onChange={(e) => setTargetDate(e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                    />
                    <Button onClick={setToday} variant="outline" size="sm">
                      Today
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Age Results */}
      {ageDetails && (
        <>
          {/* Main Age Display */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <span className="text-2xl">{getAgeEmoji(ageDetails.years)}</span>
                <span>Your Age</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-center mb-6">
                <div className="text-4xl font-bold text-primary-600 dark:text-primary-400 mb-2">
                  {ageDetails.years} years old
                </div>
                <div className="text-lg text-gray-600 dark:text-gray-400">
                  {ageDetails.years} years, {ageDetails.months} months, {ageDetails.days} days
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-500 mt-2">
                  Life Stage: {ageDetails.lifeStage}
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <div className="text-xl font-bold text-blue-600 dark:text-blue-400">
                    {ageDetails.totalDays.toLocaleString()}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Days</div>
                </div>
                
                <div className="text-center p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                  <div className="text-xl font-bold text-green-600 dark:text-green-400">
                    {ageDetails.totalWeeks.toLocaleString()}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Weeks</div>
                </div>
                
                <div className="text-center p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                  <div className="text-xl font-bold text-purple-600 dark:text-purple-400">
                    {ageDetails.totalHours.toLocaleString()}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Hours</div>
                </div>
                
                <div className="text-center p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
                  <div className="text-xl font-bold text-orange-600 dark:text-orange-400">
                    {ageDetails.totalMinutes.toLocaleString()}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Minutes</div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Birthday Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Gift className="w-5 h-5" />
                  <span>Next Birthday</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-center">
                  <div className="text-2xl font-bold text-pink-600 dark:text-pink-400 mb-2">
                    {ageDetails.daysUntilBirthday} days
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                    {ageDetails.nextBirthday.toLocaleDateString('en-US', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-500">
                    You'll turn {ageDetails.years + 1} years old
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Heart className="w-5 h-5" />
                  <span>Zodiac Signs</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Western Zodiac:</span>
                    <span className="font-medium text-purple-600 dark:text-purple-400">
                      {ageDetails.zodiacSign}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Chinese Zodiac:</span>
                    <span className="font-medium text-red-600 dark:text-red-400">
                      {ageDetails.chineseZodiac}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Fun Facts */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Clock className="w-5 h-5" />
                <span>Fun Facts</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span>Heartbeats (approx.):</span>
                    <span className="font-mono">{(ageDetails.totalMinutes * 70).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Breaths (approx.):</span>
                    <span className="font-mono">{(ageDetails.totalMinutes * 15).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Blinks (approx.):</span>
                    <span className="font-mono">{(ageDetails.totalMinutes * 17).toLocaleString()}</span>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span>Seconds lived:</span>
                    <span className="font-mono">{ageDetails.totalSeconds.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Days until 100:</span>
                    <span className="font-mono">
                      {Math.max(0, (100 - ageDetails.years) * 365 - ageDetails.totalDays % 365).toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Life progress:</span>
                    <span className="font-mono">{((ageDetails.years / 80) * 100).toFixed(1)}%</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Usage Guide */}
      <Card>
        <CardHeader>
          <CardTitle>Usage Guide</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
            <div>
              <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">Features:</h4>
              <ul className="space-y-1 text-gray-600 dark:text-gray-400">
                <li>• Precise age calculation in years, months, days</li>
                <li>• Total time lived in various units</li>
                <li>• Next birthday countdown</li>
                <li>• Western and Chinese zodiac signs</li>
                <li>• Life stage identification</li>
                <li>• Fun statistical facts</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">Options:</h4>
              <ul className="space-y-1 text-gray-600 dark:text-gray-400">
                <li>• Live calculation with current date</li>
                <li>• Calculate age as of specific date</li>
                <li>• Real-time updates every second</li>
                <li>• Accurate leap year handling</li>
                <li>• Multiple time unit displays</li>
                <li>• Birthday and milestone tracking</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
